import type { ErrorObject } from "ajv";
import {
  validateDocumentTemplateFamily,
  type CogoDocumentTemplateFamily,
} from "@cogo/schema/document-template-family-runtime";
import validateSchema from "./generated/document-template-family-schema-validator.ts";

const validateFamilySchema = validateSchema as unknown as {
  (value: unknown): boolean;
  errors?: ErrorObject[] | null;
};

export const COGO_DOCUMENT_TEMPLATE_FAMILY_SEMANTIC_VALIDATOR_ID =
  "@cogo/schema/document-template-family-runtime#validateDocumentTemplateFamily" as const;

export const COGO_DOCUMENT_TEMPLATE_FAMILY_COMBINED_ADMISSION_API_ID =
  "@cogo/schema/document-template-family-admission#validateDocumentTemplateFamilyAdmission" as const;

export interface CogoDocumentTemplateFamilySchemaIssue {
  instancePath: string;
  keyword: string;
  message: string;
}

export interface CogoDocumentTemplateFamilyAdmissionResult {
  admitted: boolean;
  schema: {
    valid: boolean;
    issues: CogoDocumentTemplateFamilySchemaIssue[];
  };
  semantic: {
    valid: boolean;
    issues: string[];
    validatorId: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_SEMANTIC_VALIDATOR_ID;
  };
}

function mapSchemaIssue(
  issue: ErrorObject,
): CogoDocumentTemplateFamilySchemaIssue {
  return {
    instancePath: issue.instancePath || "/",
    keyword: issue.keyword,
    message: issue.message ?? "JSON Schema validation failed.",
  };
}

/**
 * The sole authoritative admission API for document template family v2.
 *
 * JSON Schema intentionally handles only local structure. Cross-record slot
 * coverage, shared slot references, and id uniqueness require the semantic
 * runtime validator, so both layers execute for every admission attempt.
 */
export function validateDocumentTemplateFamilyAdmission(
  value: unknown,
): CogoDocumentTemplateFamilyAdmissionResult {
  const schemaValid = validateFamilySchema(value) === true;
  const schemaIssues = (validateFamilySchema.errors ?? []).map(mapSchemaIssue);
  const semanticIssues = validateDocumentTemplateFamily(value);
  return {
    admitted: schemaValid && semanticIssues.length === 0,
    schema: {
      valid: schemaValid,
      issues: schemaIssues,
    },
    semantic: {
      valid: semanticIssues.length === 0,
      issues: semanticIssues,
      validatorId: COGO_DOCUMENT_TEMPLATE_FAMILY_SEMANTIC_VALIDATOR_ID,
    },
  };
}

export function assertDocumentTemplateFamilyAdmission(
  value: unknown,
): asserts value is CogoDocumentTemplateFamily {
  const result = validateDocumentTemplateFamilyAdmission(value);
  if (!result.admitted) {
    const schemaMessages = result.schema.issues.map(
      (issue) => `${issue.instancePath} ${issue.message}`,
    );
    throw new Error(
      `Invalid document template family admission: ${[
        ...schemaMessages,
        ...result.semantic.issues,
      ].join(" ")}`,
    );
  }
}
